import { CABECALHO_CSRF } from './auth';
import type { Sessao } from './auth';
import { buscar } from './buscar';

/**
 * BLOCO 2 — o front do acesso falando com `/api/me`, `/api/equipe`, `/api/acesso/*` e `/api/perfis`.
 *
 * 🚨 A TELA NÃO DECIDE PERMISSÃO. As capacidades de `/api/me` servem para esconder o que não é da
 * pessoa (conveniência); quem recusa é o servidor, em cada rota. Um 403 aqui vira a tela de
 * "sem acesso" — nunca uma lista vazia fingindo que não há ninguém.
 *
 * Cada falha tem o seu nome (mesma regra do login): rede fora não é "nada encontrado".
 */

export type Eu = {
  readonly nome: string;
  readonly nomeExibicao: string;
  readonly login: string;
  readonly perfis: readonly { readonly codigo: string; readonly nome: string }[];
  readonly capacidades: readonly string[];
  /** Id da foto de perfil (0050); sem foto, `null` — a tela mostra as iniciais. */
  readonly foto?: string | null;
  /** Senha provisória (dono, 29/09): a tela abre "Crie sua senha" antes de tudo. */
  readonly acessoProvisorio?: boolean;
  /** "Só números" (08/10): o balconista não vê o que ganha nem o que vendeu em R$. */
  readonly valoresOcultos?: boolean;
};

export type SituacaoNaEquipe = 'ativo' | 'suspenso' | 'inativo' | 'aguardando_aprovacao';

export type MembroDaEquipe = {
  readonly personId: string;
  readonly accountId: string | null;
  readonly pedidoId: string | null;
  readonly nome: string;
  readonly login: string | null;
  readonly perfis: readonly string[];
  readonly situacao: SituacaoNaEquipe;
  /** Suspensão com prazo: quando o servidor libera sozinho. */
  readonly suspensoAte: string | null;
  readonly ultimoAcesso: string | null;
};

export type ConflitoDoPedido = {
  readonly tipo: 'codigo_em_uso' | 'possivel_duplicado';
  readonly descricao: string;
};

export type StatusDoPedido = 'pendente' | 'aprovado' | 'nao_aprovado';

export type PedidoDeAcesso = {
  readonly id: string;
  readonly nome: string;
  readonly whatsappMascarado: string | null;
  readonly codigoDesejado: string | null;
  readonly perfilSolicitado: string;
  readonly status: StatusDoPedido;
  readonly criadoEm: string;
  readonly decididoEm: string | null;
  readonly decididoPor: string | null;
  readonly motivoDecisao: string | null;
  readonly conflitos: readonly ConflitoDoPedido[];
};

export type PerfilComMatriz = {
  readonly codigo: string;
  readonly nome: string;
  readonly descricao: string | null;
  readonly pessoas: number;
  readonly capacidades: readonly { readonly codigo: string; readonly descricao: string }[];
};

export type Falha =
  | { readonly tipo: 'offline' }
  | { readonly tipo: 'sem-sessao' }
  | { readonly tipo: 'sem-acesso' }
  | { readonly tipo: 'conflito'; readonly mensagem: string }
  | { readonly tipo: 'invalido'; readonly mensagem: string }
  | { readonly tipo: 'limite' }
  | { readonly tipo: 'inesperado'; readonly codigo: string | null };

export type Resultado<T> =
  { readonly ok: true; readonly dados: T } | { readonly ok: false; readonly falha: Falha };

async function chamar<T>(
  caminho: string,
  opcoes: { metodo?: 'GET' | 'POST' | 'PUT'; corpo?: unknown; sessao?: Sessao } = {},
): Promise<Resultado<T>> {
  const metodo = opcoes.metodo ?? 'GET';
  let resposta: Response;
  try {
    resposta = await buscar(caminho, {
      method: metodo,
      credentials: 'same-origin',
      headers: {
        accept: 'application/json',
        ...(metodo !== 'GET' ? { 'content-type': 'application/json' } : {}),
        ...(opcoes.sessao ? { [CABECALHO_CSRF]: opcoes.sessao.csrfToken } : {}),
      },
      ...(metodo !== 'GET' ? { body: JSON.stringify(opcoes.corpo ?? {}) } : {}),
    });
  } catch {
    return { ok: false, falha: { tipo: 'offline' } };
  }
  if (resposta.status === 204) return { ok: true, dados: undefined as T };
  if (resposta.ok) return { ok: true, dados: (await resposta.json()) as T };

  const mensagem = await mensagemDoErro(resposta);
  if (resposta.status === 401) return { ok: false, falha: { tipo: 'sem-sessao' } };
  if (resposta.status === 403) return { ok: false, falha: { tipo: 'sem-acesso' } };
  if (resposta.status === 409) return { ok: false, falha: { tipo: 'conflito', mensagem } };
  if (resposta.status === 400) return { ok: false, falha: { tipo: 'invalido', mensagem } };
  if (resposta.status === 429) return { ok: false, falha: { tipo: 'limite' } };
  return {
    ok: false,
    falha: { tipo: 'inesperado', codigo: resposta.headers.get('x-request-id') },
  };
}

async function mensagemDoErro(resposta: Response): Promise<string> {
  try {
    const corpo = (await resposta.json()) as { error?: { message?: string } };
    return corpo.error?.message ?? '';
  } catch {
    return '';
  }
}

export const buscarEu = () => chamar<Eu>('/api/me');

export const listarEquipe = () => chamar<{ pessoas: readonly MembroDaEquipe[] }>('/api/equipe');

export const listarPedidos = (status: StatusDoPedido) =>
  chamar<{ pedidos: readonly PedidoDeAcesso[] }>(`/api/acesso/pedidos?status=${status}`);

export const listarPerfis = () => chamar<{ perfis: readonly PerfilComMatriz[] }>('/api/perfis');

export const aprovarPedido = (sessao: Sessao, id: string, login: string) =>
  chamar<{ accountId: string }>(`/api/acesso/pedidos/${id}/aprovar`, {
    metodo: 'POST',
    corpo: { login },
    sessao,
  });

export const recusarPedido = (sessao: Sessao, id: string, motivo: string) =>
  chamar<void>(`/api/acesso/pedidos/${id}/recusar`, { metodo: 'POST', corpo: { motivo }, sessao });

/** `dias` ausente = até alguém reativar; com dias = a "falta" do 1.0 (o servidor libera sozinho). */
export const suspenderConta = (
  sessao: Sessao,
  accountId: string,
  motivo: string,
  dias: number | null = null,
) =>
  chamar<{ status: string }>(`/api/equipe/${accountId}/suspender`, {
    metodo: 'POST',
    corpo: dias ? { motivo, dias } : { motivo },
    sessao,
  });

export type FuncaoDaEquipe = 'balconista' | 'admin_geral' | 'auditor' | 'receptor';
export const trocarFuncao = (sessao: Sessao, accountId: string, funcao: FuncaoDaEquipe) =>
  chamar<{ funcao: FuncaoDaEquipe; mudou: boolean }>(`/api/equipe/${accountId}/funcao`, {
    metodo: 'POST',
    corpo: { funcao },
    sessao,
  });

/** Editar nome e código (o 1.0 deixava). A senha NÃO é campo: só a pessoa ou o terminal. */
export const editarPessoa = (
  sessao: Sessao,
  accountId: string,
  dados: { nome?: string; nomeExibicao?: string; login?: string },
) =>
  chamar<{ nome: string; nomeExibicao: string; login: string }>(`/api/equipe/${accountId}/pessoa`, {
    metodo: 'PUT',
    corpo: dados,
    sessao,
  });

export const reativarConta = (sessao: Sessao, accountId: string) =>
  chamar<{ status: string }>(`/api/equipe/${accountId}/reativar`, { metodo: 'POST', sessao });

export const trocarMinhaSenha = (sessao: Sessao, senhaAtual: string, novaSenha: string) =>
  chamar<{ outrasSessoesEncerradas: number }>('/api/me/senha', {
    metodo: 'POST',
    corpo: { senhaAtual, novaSenha },
    sessao,
  });

/** Porta pública (tela 04). Sem sessão; a resposta é sempre a mesma, de propósito. */
export const solicitarAcesso = (pedido: {
  nome: string;
  whatsapp: string;
  codigo: string;
  senha: string;
}) =>
  chamar<{ recebido: true }>('/api/acesso/pedidos', {
    metodo: 'POST',
    corpo: {
      nome: pedido.nome,
      whatsapp: pedido.whatsapp || null,
      codigo: pedido.codigo || null,
      senha: pedido.senha,
    },
  });

/* ------------------------------------------------------------------ *
 * Formatação comum das telas de acesso
 * ------------------------------------------------------------------ */

/** "Maria Olívia Fernandes" → "MO". Sem nome, um ponto — nunca iniciais inventadas. */
export function iniciais(nome: string): string {
  const partes = nome.trim().split(/\s+/).filter(Boolean);
  const a = partes[0]?.[0] ?? '';
  const b = partes.length > 1 ? (partes[1]?.[0] ?? '') : (partes[0]?.[1] ?? '');
  return (a + b).toUpperCase() || '·';
}

/** Primeiro nome, para a topbar ("Carlos"). */
export function primeiroNome(nome: string): string {
  return nome.trim().split(/\s+/)[0] ?? nome;
}

export function podeFazer(eu: Eu | null, capacidade: string): boolean {
  return eu?.capacidades.includes(capacidade) ?? false;
}

const HORA = new Intl.DateTimeFormat('pt-BR', { hour: '2-digit', minute: '2-digit' });
const DIA_MES = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' });

/** "agora" · "hoje 07:38" · "ontem 19:12" · "12/09 18:40". */
export function quando(iso: string, agora: Date = new Date()): string {
  const d = new Date(iso);
  const minutos = (agora.getTime() - d.getTime()) / 60_000;
  if (minutos >= 0 && minutos < 2) return 'agora';
  const dia = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dias = Math.round((dia(agora) - dia(d)) / 86_400_000);
  if (dias === 0) return `hoje ${HORA.format(d)}`;
  if (dias === 1) return `ontem ${HORA.format(d)}`;
  return `${DIA_MES.format(d)} ${HORA.format(d)}`;
}

/** "há 2 dias" · "ontem" · "hoje 11:20" — a idade de um pedido na fila. */
export function idade(iso: string, agora: Date = new Date()): string {
  const d = new Date(iso);
  const dia = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dias = Math.round((dia(agora) - dia(d)) / 86_400_000);
  if (dias <= 0) return `hoje ${HORA.format(d)}`;
  if (dias === 1) return 'ontem';
  return `há ${String(dias)} dias`;
}

export function dataHora(iso: string): string {
  const d = new Date(iso);
  return `${DIA_MES.format(d)} às ${HORA.format(d)}`;
}

export function dataHoraCurta(iso: string): string {
  const d = new Date(iso);
  return `${DIA_MES.format(d)} ${HORA.format(d)}`;
}

/* ------------------------------------------------------------------ *
 * Destaques do Início: o pódio do mês e os aniversariantes
 * ------------------------------------------------------------------ */

/** O pódio do mês (dono, 27/09/2026): os 3 que mais venderam — só NOME e POSIÇÃO, sem valor. */
export type Destaques = {
  readonly vendedoresDoMes: {
    readonly mes: string;
    readonly podio: readonly { readonly posicao: number; readonly nome: string }[];
  };
  readonly aniversariantes: readonly string[];
};
export const lerDestaques = () => chamar<Destaques>('/api/destaques');
export type Aniversario = { readonly dia: number; readonly mes: number } | null;
export const lerMeuAniversario = () => chamar<{ aniversario: Aniversario }>('/api/me/aniversario');
export const salvarMeuAniversario = (sessao: Sessao, aniversario: Aniversario) =>
  chamar<{ aniversario: Aniversario }>('/api/me/aniversario', {
    metodo: 'PUT',
    corpo: { aniversario },
    sessao,
  });

/* ------------------------------------------------------------------ *
 * Equipe — cadastrar, senha PROVISÓRIA e excluir (dono, 29/09)
 * ------------------------------------------------------------------ */

export const cadastrarPessoa = (
  sessao: Sessao,
  d: { nome: string; login: string; senhaProvisoria: string; funcao: FuncaoDaEquipe },
) => chamar<{ accountId: string }>('/api/equipe', { metodo: 'POST', corpo: d, sessao });
export const darSenhaProvisoria = (sessao: Sessao, accountId: string, senhaProvisoria: string) =>
  chamar<{ sessoesEncerradas: number }>(`/api/equipe/${accountId}/senha-provisoria`, {
    metodo: 'POST',
    corpo: { senhaProvisoria },
    sessao,
  });
export const excluirConta = (sessao: Sessao, accountId: string, motivo: string) =>
  chamar<{ status: string }>(`/api/equipe/${accountId}/excluir`, {
    metodo: 'POST',
    corpo: { motivo },
    sessao,
  });
